/**
 * ShareButton.tsx — T-500 "Share" control (Noreen item 8, option A: authed deep-link).
 *
 * Turns the CURRENT location into a copyable authed deep-link. `getShareUrl` is the hook's
 * single-source-of-truth builder (useMapUrlState.getShareUrl), evaluated at click time so the link
 * always matches what is on screen. The link encodes only the location (viewport + tract GEOID +
 * pin) — never a score — and, because the app is router-less and the state lives in the URL hash,
 * it survives the Cognito login wall: an unauthenticated recipient signs in / signs up and lands on
 * the shared location. That acquisition-funnel step is why a copy emits `share_link_created`.
 */
import { useEffect, useRef, useState } from 'react'
import { Share2, Check } from 'lucide-react'
import { copyTextToClipboard } from '../lib/shareLink'
import { toast } from './ui/toaster'
import { track } from '../lib/analytics'

export interface ShareButtonProps {
  /** Returns the absolute authed deep-link for the CURRENT location, evaluated at click time. */
  getShareUrl: () => string
  className?: string
}

const COPIED_RESET_MS = 2000

export function ShareButton({ getShareUrl, className }: ShareButtonProps) {
  const [copied, setCopied] = useState(false)
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clear a pending "Copied → Share" reset on unmount so it never sets state on a gone component.
  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current)
    },
    [],
  )

  async function onShare() {
    const url = getShareUrl()
    const ok = await copyTextToClipboard(url)
    if (!ok) {
      toast.error('Could not copy the link. You can copy it from your browser address bar.', {
        id: 'share-link',
        duration: 6000,
      })
      return
    }
    setCopied(true)
    if (resetTimer.current) clearTimeout(resetTimer.current)
    resetTimer.current = setTimeout(() => setCopied(false), COPIED_RESET_MS)
    toast.success('Link copied. Whoever you send it to will sign in to view this location.', {
      id: 'share-link',
      duration: 4000,
    })
    // Funnel measurement (T-500): coarse only — a hash with any &-param carried a tract/pin
    // selection; a bare #zoom/lat/lng is viewport-only. The location itself never leaves here.
    track('share_link_created', { has_selection: url.includes('&') })
  }

  return (
    <button
      type="button"
      onClick={() => void onShare()}
      aria-label="Copy a shareable link to this location"
      className={
        'flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground ' +
        'hover:bg-accent hover:text-accent-foreground focus-visible:outline-none ' +
        'focus-visible:ring-2 focus-visible:ring-ring' +
        (className ? ' ' + className : '')
      }
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      ) : (
        <Share2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      )}
      <span className="hidden sm:inline">{copied ? 'Copied' : 'Share'}</span>
    </button>
  )
}
